import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { TableLineageModal } from "datasets/components/TableLineageModal";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import usePermissions from "datasets/hooks/usePermissions";
import { removeDatasetTable } from "datasets/pages/tables/api/modifyDatasetTables";
import useDatasetTables from "datasets/pages/tables/hooks/useDatasetTables";
import useTrackDatasetTablesViewed from "datasets/pages/tables/hooks/useTrackDatasetTablesViewed";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { MoreHorizontal, Search, X } from "lucide-react";
import { getCodeSnippet } from "~/components/GenerateCodeButton";
import { TablePreviewModal } from "~/components/table-preview/TablePreviewModal";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import TableAccessLabel from "~/pages/manifold-nextgen/components/datasets/pages/tables/TableAccessLabel";

const TableSkeleton = () => (
  <div role="status" aria-label="Loading tables" className="flex flex-col gap-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-16 w-full rounded-md" />
    ))}
  </div>
);

const TableRow = ({ table, search }: { table: DatasetTableResponse; search: string }) => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const { data: dataset, mutate: mutateDataset } = useDataset();
  const { mutate: mutateFQNs } = useDatasetTableFQNs();
  const { mutate: mutateTables } = useDatasetTables(search);
  const canEdit = usePermissions("edit:catalog-metadata");
  const [, copy] = useCopyToClipboard();
  const { showSuccess } = useToast();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [lineageOpen, setLineageOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeError, setRemoveError] = useState("");

  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const handleCopyCode = () => {
    copy(getCodeSnippet("r", [{ schema: table.database_schema, table: table.name }]));
    showSuccess("Code copied to clipboard");
  };

  const handleRemove = async () => {
    if (!datasetId) {
      return;
    }
    setRemoveError("");
    try {
      await removeDatasetTable(datasetId, table.fullyQualifiedName);
      mutateFQNs();
      mutateTables();
      mutateDataset();
    } catch (e: any) {
      setRemoveError(e.message ?? "Unable to remove table from dataset");
      throw e;
    }
  };

  return (
    <div className="group relative flex flex-col gap-1 border-b border-border py-4 last:border-0">
      <Link
        to={`/nextgen/datasets/${datasetId}/tables/${table.fullyQualifiedName}`}
        className="text-sm font-semibold text-primary hover:underline"
      >
        {table.name}
      </Link>
      <p className="text-xs text-muted-foreground">
        {table.database_schema}
        {table.tableType && (
          <>
            {" · "}
            <span>{table.tableType === "Regular" ? "Table" : table.tableType}</span>
          </>
        )}
        {table.accessLevel && (
          <>
            {" · "}
            <TableAccessLabel
              accessLevel={table.accessLevel}
              isView={/view/i.test(table.tableType ?? "")}
            />
          </>
        )}
      </p>
      <p className="line-clamp-2 text-sm text-muted-foreground">
        {table.description || <span className="italic">No description</span>}
      </p>
      <div>
        <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
          View Data
        </Button>
      </div>

      <div className="absolute right-0 top-4 opacity-0 transition-opacity group-hover:opacity-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <MoreHorizontal className="size-4" />
              <span className="sr-only">Table actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isOwner && (
              <DropdownMenuItem onClick={() => setLineageOpen(true)}>View Lineage</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleCopyCode}>Copy Code</DropdownMenuItem>
            {canEdit && (
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setRemoveOpen(true)}
              >
                Remove
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TablePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        tableFqn={table.fullyQualifiedName}
        tableName={table.name}
      />
      <TableLineageModal
        open={lineageOpen}
        onOpenChange={setLineageOpen}
        tableFQN={table.fullyQualifiedName}
        tableName={table.name}
      />
      <ConfirmDestructiveDialog
        target={removeOpen ? table : null}
        title={`Remove "${table.name}"`}
        description={() => (
          <>
            <span className="block">Remove the association between the dataset and the table?</span>
            <span className="mt-2 block">
              The table will continue to exist. Unless the table is associated with another of your
              datasets, you will also lose your permission to the table.
            </span>
          </>
        )}
        confirmLabel="Remove"
        pendingLabel="Removing..."
        onConfirm={handleRemove}
        onClose={() => {
          setRemoveOpen(false);
          // Drop the failure with the dialog, so reopening it doesn't lead with
          // an error from an attempt the user already dismissed.
          setRemoveError("");
        }}
        error={removeError ? <p className="text-sm text-destructive">{removeError}</p> : undefined}
      />
    </div>
  );
};

export const DatasetTableList = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const [search, setSearch] = useState("");
  const { data: tables, isLoading } = useDatasetTables(search);

  useTrackDatasetTablesViewed(search, datasetId);

  const showEmpty = !isLoading && (!tables || tables.length === 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
        <Input
          placeholder="Search tables..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 pr-8"
        />
        {search && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1 size-7"
            onClick={() => setSearch("")}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : showEmpty ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {search ? "No tables match your search." : "No tables in this dataset."}
        </p>
      ) : (
        <div>
          {tables?.map((table) => (
            <TableRow key={table.fullyQualifiedName} table={table} search={search} />
          ))}
        </div>
      )}
    </div>
  );
};
